/**
 * Pure engine for per-user table column preferences (visibility + order).
 *
 * This module is deliberately React-free so every rule (reconcile/migrate,
 * visibility derivation, storage I/O) is trivially unit-testable. The React
 * hook `useTableColumnPrefs` wires these functions to component state and to
 * the `@atrvd/saas-ui` DataTable controlled props.
 *
 * Persisted shape is versioned so that a future change to the storage format
 * can invalidate incompatible saved blobs instead of misinterpreting them.
 */

/**
 * Persisted preferences for a single table.
 *
 * - `order`  — full ordered list of column keys (drives DataTable `columnOrder`).
 * - `hidden` — subset of keys the user has hidden (everything else is visible).
 * - `version` — storage schema version; mismatches are discarded on read.
 */
export interface ColumnPrefs {
  version: number
  order: string[]
  hidden: string[]
}

/** Current storage schema version. Bump to invalidate older saved blobs. */
export const CURRENT_VERSION = 1

/**
 * Return a copy of `arr` with duplicates removed, preserving first-seen order
 * (later duplicates are dropped). Used to keep `order`/`hidden` free of the
 * duplicate keys a corrupted/legacy blob could otherwise introduce.
 */
function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)]
}

/**
 * Type guard: does an unknown value look like a well-formed ColumnPrefs?
 * Used by both reconcile() and readPrefs() so a corrupted/legacy blob is
 * treated as "no preferences" rather than crashing consumers.
 */
function isColumnPrefs(value: unknown): value is ColumnPrefs {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.version === 'number' &&
    Array.isArray(candidate.order) &&
    candidate.order.every((k) => typeof k === 'string') &&
    Array.isArray(candidate.hidden) &&
    candidate.hidden.every((k) => typeof k === 'string')
  )
}

/**
 * Reconcile saved preferences against the current set of column keys.
 *
 * This is the migration heart of the feature: it keeps the user's intent while
 * absorbing columns that were added or removed since the blob was written.
 *
 * @param saved         Previously persisted prefs, or null when nothing is stored.
 * @param allKeys       Current column keys, in the code-defined default order.
 * @param mandatoryKeys Keys that can never be hidden (`disableHide` columns).
 *                      A mandatory key is force-shown even if a saved blob or a
 *                      "new column" rule would otherwise place it in `hidden`.
 * @param optInKeys     Keys whose column sets `defaultVisible: true` — they are
 *                      shown rather than hidden when they first appear. Opt-in
 *                      per column, so the hide-by-default rule still governs
 *                      every column that does not ask for the exception.
 * @returns A normalized ColumnPrefs safe to feed to the DataTable.
 *
 * Rules:
 * - Fresh (null / version mismatch / malformed): default order, ALL visible.
 * - Otherwise: dropped columns are removed; NEW columns are appended at the
 *   END of the order and default to HIDDEN (so tables don't silently widen),
 *   EXCEPT mandatory columns (never hidden) and `defaultVisible` opt-ins.
 *   `order` is deduped so a corrupted blob can never yield doubled columns /
 *   duplicate React keys.
 *
 * WHY THE OPT-IN EXISTS (DCP-320). The hook persists a blob on first mount, so
 * EVERY user who has ever opened a table has a saved blob — which means the
 * hide-by-default rule makes a column added by a later ticket invisible for
 * the entire existing user base, and the feature ships dark. "Don't silently
 * widen" is right for a column nobody asked for; a column that IS the ticket
 * has to arrive visible. Marking it `defaultVisible` on the column definition
 * says exactly that, once, next to the column.
 */
export function reconcile(
  saved: ColumnPrefs | null,
  allKeys: string[],
  mandatoryKeys: string[] = [],
  optInKeys: string[] = []
): ColumnPrefs {
  // Fresh start: no valid saved prefs -> default order, everything visible.
  if (!saved || saved.version !== CURRENT_VERSION || !isColumnPrefs(saved)) {
    return { version: CURRENT_VERSION, order: [...allKeys], hidden: [] }
  }

  // Preserve the saved order for still-existing columns, then append any
  // columns that did not exist when the blob was saved (new since then).
  // `unique` guarantees the result is a duplicate-free permutation of allKeys.
  const order = unique([
    ...saved.order.filter((k) => allKeys.includes(k)),
    ...allKeys.filter((k) => !saved.order.includes(k)),
  ])

  // Columns added since the config was saved — default these to hidden, unless
  // the column opted in to arriving visible (`defaultVisible`). The opt-in only
  // applies on ARRIVAL: once the key is in `saved.order` it is no longer new, so
  // a user who then hides it keeps it hidden on every later render.
  const newKeys = allKeys.filter(
    (k) => !saved.order.includes(k) && !optInKeys.includes(k)
  )

  // Dedupe, then strip mandatory keys so a `disableHide` column is never hidden
  // (neither a newly-added mandatory column nor one lurking in a stale blob).
  const hidden = unique([
    ...saved.hidden.filter((k) => allKeys.includes(k)),
    ...newKeys,
  ]).filter((k) => !mandatoryKeys.includes(k))

  return { version: CURRENT_VERSION, order, hidden }
}

/**
 * Derive the set of visible column keys from prefs.
 * Iterates `order` (not `allKeys`) so callers see keys in persisted order.
 */
export function toVisibleSet(prefs: ColumnPrefs): Set<string> {
  return new Set(prefs.order.filter((k) => !prefs.hidden.includes(k)))
}

/**
 * Apply a new visibility set, recomputing `hidden` as the complement of
 * `visible` across all known keys. Order is preserved unchanged.
 *
 * @param prefs         Current prefs (order is kept as-is).
 * @param visible       Set of keys that should now be visible.
 * @param allKeys       All known column keys (defines the universe for `hidden`).
 * @param mandatoryKeys Keys that can never be hidden; excluded from `hidden`
 *                      even if a stray visibility set omits them.
 */
export function withVisible(
  prefs: ColumnPrefs,
  visible: Set<string>,
  allKeys: string[],
  mandatoryKeys: string[] = []
): ColumnPrefs {
  const hidden = allKeys.filter((k) => !visible.has(k) && !mandatoryKeys.includes(k))
  return { version: prefs.version, order: prefs.order, hidden }
}

/**
 * Apply a new column order. Visibility (`hidden`) is preserved unchanged.
 */
export function withOrder(prefs: ColumnPrefs, order: string[]): ColumnPrefs {
  return { version: prefs.version, order, hidden: prefs.hidden }
}

/**
 * Build the localStorage key for a given user + table pair.
 * Namespacing by user id keeps preferences per-account on shared machines.
 */
export function storageKey(userId: string, tableId: string): string {
  return `dcp:cols:${userId}:${tableId}`
}

/**
 * Read + parse persisted prefs. Returns null on any failure (missing key,
 * unavailable storage in private mode, invalid JSON, malformed shape) so the
 * caller can degrade to in-memory defaults. Never throws.
 */
export function readPrefs(key: string): ColumnPrefs | null {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    return isColumnPrefs(parsed) ? parsed : null
  } catch {
    // Storage disabled/quota/JSON error -> behave as if nothing was saved.
    return null
  }
}

/**
 * Persist prefs. Silent no-op on any failure (quota exceeded, private mode)
 * so preference-saving can never break the app. Never throws.
 */
export function writePrefs(key: string, prefs: ColumnPrefs): void {
  try {
    localStorage.setItem(key, JSON.stringify(prefs))
  } catch {
    // Storage disabled/quota exceeded -> keep working with in-memory state.
  }
}
